import React, { useRef, useState } from 'react';
import Icon from '../shared/Icon';
import {
  LIBRARY_FILE_ACCEPT,
  LIBRARY_FILE_LABEL,
  libraryFileProblem,
  formatFileSize,
} from '../../../models/LibraryModel';
import './Library.css'; // library-sr-only, library-icon-trigger
import './LibraryDropZone.css';

/**
 * The Add document file slot (PRD-1606): one file, dropped or picked, and the
 * chosen file listed under the zone with its size and a way to remove it.
 * Nothing is parsed; the extension is checked and the size capped, and a file
 * that fails either is reported through `onProblem` and not taken.
 *
 * `labelId` is the visible "File" label, which names both the hidden picker
 * and the Select from computer button.
 */
function LibraryDropZone({ file, labelId, onFile, onClear, onProblem }) {
  const pickerRef = useRef(null);
  const [dragDepth, setDragDepth] = useState(0);

  const take = (picked) => {
    if (!picked) return;
    const problem = libraryFileProblem(picked);
    if (problem) { onProblem(problem); return; }
    onFile({ name: picked.name, size: picked.size });
  };

  // dragenter/dragleave fire for every child the pointer crosses, so count them
  // rather than toggling a boolean that flickers (as FileUploadZone does).
  const dragHandlers = {
    onDragEnter: (e) => { e.preventDefault(); setDragDepth((d) => d + 1); },
    onDragOver: (e) => e.preventDefault(),
    onDragLeave: (e) => { e.preventDefault(); setDragDepth((d) => Math.max(0, d - 1)); },
    onDrop: (e) => {
      e.preventDefault();
      setDragDepth(0);
      take(e.dataTransfer?.files?.[0]);
    },
  };

  return (
    <>
      <input
        ref={pickerRef}
        type="file"
        className="library-sr-only"
        accept={LIBRARY_FILE_ACCEPT}
        aria-labelledby={labelId}
        tabIndex={-1}
        onChange={(e) => { take(e.target.files?.[0]); e.target.value = ''; }}
      />
      <div className={`library-drop${dragDepth > 0 ? ' is-over' : ''}`} {...dragHandlers}>
        <Icon name="cloud-arrow-up" size={28} className="library-drop-icon" />
        <div className="library-drop-hint">Drag and drop a file here</div>
        <div className="library-drop-sub">{LIBRARY_FILE_LABEL}</div>
        <button
          type="button"
          className="resui-btn resui-btn--tertiary resui-btn--small"
          aria-describedby={labelId}
          onClick={() => pickerRef.current?.click()}
        >
          Select from computer
        </button>
      </div>
      {file && (
        <div className="library-file-row">
          <Icon name="file-text" size={20} className="library-file-icon" />
          <span className="library-file-name">{file.name}</span>
          <span className="library-file-size">{formatFileSize(file.size)}</span>
          <button type="button" className="library-icon-trigger" aria-label="Remove file" onClick={onClear}>
            <Icon name="x" size={20} />
          </button>
        </div>
      )}
    </>
  );
}

export default LibraryDropZone;
